import React, { useState, useEffect, useRef, useCallback } from 'react';

export interface SendCodeButtonProps {
  /** 倒计时时长（秒），默认 60 秒 */
  seconds?: number;
  /** 发送验证码处理函数，返回 false 或抛出异常时不会启动倒计时 */
  onSend?: () => Promise<boolean | void> | boolean | void;
  /** 点击事件 (如果未传 onSend 时生效或用于叠加点击事件) */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 额外的禁用条件 (如手机号未填写或格式校验未通过) */
  disabled?: boolean;
  /** 外部控制倒计时数值 (传入时使用外部数值) */
  countdown?: number;
  /** 外部控制发送中状态 */
  sending?: boolean;
  /** 默认状态文案，默认 '获取验证码' */
  text?: string;
  /** 重新发送文案 (若不指定则仍用 text) */
  reText?: string;
  /** 发送中状态文案，默认 '发送中...' */
  sendingText?: string;
  /** 倒计时文案，支持传入函数 `(s) => `${s}s``，或字符串带 `{s}` 占位符如 `"{s}s"` */
  countingText?: string | ((seconds: number) => string);
  /** 倒计时结束回调 */
  onCountdownEnd?: () => void;
  /** 自定义样式类名 */
  className?: string;
  /** 按钮类型 */
  type?: 'button' | 'submit' | 'reset';
  /** 自定义渲染子节点 */
  children?: React.ReactNode;
}

export const SendCodeButton: React.FC<SendCodeButtonProps> = ({
  seconds = 60,
  onSend,
  onClick,
  disabled = false,
  countdown,
  sending,
  text = '获取验证码',
  reText,
  sendingText = '发送中...',
  countingText,
  onCountdownEnd,
  className = 'px-3 py-3 rounded-lg bg-gray-100 border border-gray-200 text-xs font-medium text-foreground-700 hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed',
  type = 'button',
  children,
}) => {
  const isControlled = typeof countdown === 'number';
  const [internalCount, setInternalCount] = useState<number>(0);
  const [internalSending, setInternalSending] = useState<boolean>(false);
  const [hasSent, setHasSent] = useState<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentCount = isControlled ? countdown : internalCount;
  const currentSending = typeof sending === 'boolean' ? sending : internalSending;

  // 清除定时器
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 内部倒计时逻辑
  useEffect(() => {
    if (!isControlled) {
      if (internalCount > 0) {
        timerRef.current = setInterval(() => {
          setInternalCount((prev) => {
            if (prev <= 1) {
              clearTimer();
              onCountdownEnd?.();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        clearTimer();
      }
    }

    return () => {
      clearTimer();
    };
  }, [internalCount, isControlled, clearTimer, onCountdownEnd]);

  // 处理点击发送
  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);

    if (currentCount > 0 || currentSending || disabled) {
      return;
    }

    if (onSend) {
      setInternalSending(true);
      try {
        const result = await onSend();
        // 如果明确返回 false，代表校验或发送失败，不开启倒计时
        if (result !== false) {
          setHasSent(true);
          if (!isControlled) {
            setInternalCount(seconds);
          }
        }
      } catch (err) {
        // 捕获异常，不开启倒计时
      } finally {
        setInternalSending(false);
      }
    }
  };

  // 渲染文本内容
  const renderContent = () => {
    if (children) {
      return children;
    }
    if (currentSending) {
      return sendingText;
    }
    if (currentCount > 0) {
      if (typeof countingText === 'function') {
        return countingText(currentCount);
      }
      if (typeof countingText === 'string') {
        return countingText.includes('{s}')
          ? countingText.replace('{s}', String(currentCount))
          : countingText;
      }
      return `${currentCount}s`;
    }
    if (hasSent && reText) {
      return reText;
    }
    return text;
  };

  const isDisabled = disabled || currentCount > 0 || currentSending;

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={isDisabled}
      className={className}
    >
      {renderContent()}
    </button>
  );
};

export default SendCodeButton;
